/* iter329 Faz 2 — Vergi Hesabı Full Frontend.
 *
 * Tek sayfa — bağımsız; mevcut muhasebe verilerinden READ=0, WRITE=0.
 * Permission gating: tax_calculation.view/edit/admin (admin bypass).
 *
 * Sections:
 *   A — Üst: Vergi & SGK Borç Durumu + Yönetim (modal)
 *   B — Hesaplama Oranları (collapsible) + DİBS tablosu
 *   C — Ay/Yıl seçimi
 *   D/E/F — META / PINUS / LAVION kartları (opening + inputs + computed)
 *   G — Genel Giderler (row-based)
 *   H — SGK kartı 3-firma breakdown
 *   I — Aylık Genel Özet (3 firma toplam kamu yükü)
 */
import React, { useState, useEffect, useCallback, useMemo } from "react";
import { api } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { AlertTriangle, Save, Loader2, Info, Plus, Trash2,
          ChevronDown, ChevronRight, Edit3, X, CheckCircle } from "lucide-react";
import { useWidgetOrder, WidgetWrapper } from "@/components/WidgetReorder";

const COMPANIES = ["META", "PINUS", "LAVION"];
const MONTHS = ["Ocak","Şubat","Mart","Nisan","Mayıs","Haziran",
                "Temmuz","Ağustos","Eylül","Ekim","Kasım","Aralık"];
const money = (v) => (Number(v || 0)).toLocaleString("tr-TR", {
  minimumFractionDigits: 2, maximumFractionDigits: 2 });

const canEdit = (u) => u?.role === "admin" || u?.system_authority ||
    (u?.action_permissions || {})["tax_calculation.edit"] ||
    (u?.action_permissions || {})["tax_calculation.admin"];
const canAdmin = (u) => u?.role === "admin" || u?.system_authority ||
    (u?.action_permissions || {})["tax_calculation.admin"];

// iter329.8 — Vergi Hesabı sayfa düzeni (ortak, admin düzenler)
const VH_DEFAULT_ORDER = [
  "debt_summary", "upcoming_payments", "overdue_payments",
  "period_selector", "rates", "dibs",
  "company_tax_cards", "monthly_summary", "paid_history",
];
const VH_DEFAULT_SIZES = Object.fromEntries(
  VH_DEFAULT_ORDER.map(k => [k, "FULL"]));
const VH_SECTION_TITLES = {
  debt_summary: "Vergi & SGK Borç Durumu",
  upcoming_payments: "Yaklaşan Vergi & SGK Ödemeleri",
  overdue_payments: "Gecikmiş Vergi & SGK",
  period_selector: "Dönem Seçici",
  rates: "Hesaplama Oranları",
  dibs: "DİBS Oranları",
  company_tax_cards: "META / PINUS / LAVION Vergi Hesapları",
  monthly_summary: "Aylık Genel Özet",
  paid_history: "Ödenen Vergi & SGK Geçmişi",
};

export default function VergiHesabiTab() {
  const { user } = useAuth();
  const now = new Date();
  const [year, setYear] = useState(now.getFullYear());
  const [month, setMonth] = useState(now.getMonth() + 1);
  const [reload, setReload] = useState(0);
  const tick = useCallback(() => setReload(x => x + 1), []);
  const admin = canAdmin(user);
  const edit = canEdit(user);

  const wo = useWidgetOrder(VH_DEFAULT_ORDER, VH_DEFAULT_SIZES, "tax_calculation");
  const canReorder = admin && wo.canEdit;
  const [snapshot, setSnapshot] = useState(null);

  const enterEditMode = () => {
    setSnapshot({
      order: [...wo.order],
      hidden: Array.from(wo.hidden),
      sizes: { ...wo.sizes },
    });
    wo.setEditMode(true);
  };
  const cancelEdits = () => {
    if (snapshot) {
      wo.setOrder(snapshot.order);
      wo.setHidden(new Set(snapshot.hidden));
      wo.setSizes(snapshot.sizes);
    }
    setSnapshot(null);
    wo.setEditMode(false);
  };
  const saveEdits = () => {
    setSnapshot(null);
    wo.setEditMode(false);
  };
  const resetDefault = () => {
    if (!window.confirm("Varsayılan düzene dönülecek. Emin misiniz?")) return;
    wo.resetOrder();
  };

  const sections = {
    debt_summary: <DebtSummaryPanel reload={reload} admin={admin} onChange={tick}/>,
    upcoming_payments: <UpcomingObligationsPanel reload={reload} edit={edit} onChange={tick}/>,
    overdue_payments: <OverdueObligationsPanel reload={reload} edit={edit} admin={admin} onChange={tick}/>,
    period_selector: <PeriodSelector year={year} month={month} setYear={setYear} setMonth={setMonth}/>,
    rates: <RatesPanel admin={admin}/>,
    dibs: <DibsPanel admin={admin}/>,
    company_tax_cards: (
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-3">
        {COMPANIES.map(c => (
          <CompanyBlock key={c} company={c} year={year} month={month}
                          edit={edit} admin={admin} onObligationChange={tick}/>
        ))}
      </div>
    ),
    monthly_summary: <MonthlySummaryPanel year={year} month={month} key={`sum-${year}-${month}-${reload}`}/>,
    paid_history: <PaidHistoryPanel reload={reload} admin={admin} onChange={tick}/>,
  };

  const dragStart = (e, idx) => {
    try { e.dataTransfer.setData("text/plain", String(idx)); } catch(_){}
  };
  const dragOver = (e) => { e.preventDefault(); };
  const drop = (e, idx) => {
    e.preventDefault();
    const from = parseInt(e.dataTransfer.getData("text/plain"), 10);
    if (isNaN(from) || from === idx) return;
    const srcKey = wo.order[from];
    const dstKey = wo.order[idx];
    if (srcKey && dstKey) wo.reorderTo(srcKey, dstKey);
  };

  return (
    <div className="space-y-3" data-testid="vergi-hesabi-tab">
      <div className="p-3 rounded-lg bg-amber-50 border border-amber-300 text-xs text-amber-900 flex items-start gap-2">
        <Info size={14} className="mt-0.5 flex-shrink-0"/>
        <div>
          <b>Tahmini hesaplamadır.</b> Resmî beyanname ve muhasebe kaydı yerine
          geçmez. Bu sayfa MetaERP'nin diğer modüllerinden veri çekmez.
        </div>
      </div>

      {canReorder && (
        <div className="flex items-center justify-end gap-2 bg-slate-50 border rounded px-3 py-1.5">
          {wo.editMode && (
            <span className="text-[10px] text-slate-500 mr-auto italic">
              Düzenleme modu — başlıktan sürükleyin veya ↑↓ ok butonlarını kullanın
            </span>
          )}
          {wo.editMode ? (
            <>
              <button onClick={resetDefault} className="text-xs px-2 py-1 bg-slate-200 hover:bg-slate-300 rounded"
                data-testid="vh-layout-reset">Varsayılan Düzeni Geri Yükle</button>
              <button onClick={cancelEdits} className="text-xs px-2 py-1 bg-rose-100 hover:bg-rose-200 text-rose-700 rounded"
                data-testid="vh-layout-cancel">İptal</button>
              <button onClick={saveEdits} className="text-xs px-2 py-1 bg-emerald-600 hover:bg-emerald-700 text-white rounded"
                data-testid="vh-layout-save">Kaydet</button>
            </>
          ) : (
            <button onClick={enterEditMode} className="text-xs px-2 py-1 bg-indigo-600 hover:bg-indigo-700 text-white rounded"
              data-testid="vh-layout-edit">⇅ Sayfa Düzenini Düzenle</button>
          )}
        </div>
      )}

      <div className="grid grid-cols-4 gap-3">
        {wo.order.filter(k => sections[k]).map((k, i, arr) => (
          <WidgetWrapper
            key={k}
            id={k}
            title={VH_SECTION_TITLES[k] || k}
            editMode={wo.editMode}
            isFirst={i === 0}
            isLast={i === arr.length - 1}
            isHidden={wo.hidden.has(k)}
            size={wo.sizes[k] || "FULL"}
            onMove={(id, dir) => wo.move(id, dir)}
            onToggleHide={(id) => wo.toggleHidden(id)}
            onCycleSize={null}
            onDragStart={(e) => dragStart(e, i)}
            onDragOver={dragOver}
            onDrop={(e) => drop(e, i)}
          >
            {sections[k]}
          </WidgetWrapper>
        ))}
      </div>
    </div>
  );
}

/* ─── Section C — Period Selector ─────────────────────────────────────── */
function PeriodSelector({ year, month, setYear, setMonth }) {
  const now = new Date();
  return (
    <div className="flex items-end gap-2 bg-white border rounded p-2">
      <label className="block">
        <span className="text-[10px] uppercase font-bold text-slate-500">Yıl</span>
        <select value={year} onChange={e => setYear(+e.target.value)}
                className="block border rounded px-2 py-1 text-sm" data-testid="vh-year">
          {Array.from({length:8},(_,i)=>now.getFullYear()-3+i).map(y=>
            <option key={y} value={y}>{y}</option>)}
        </select>
      </label>
      <label className="block">
        <span className="text-[10px] uppercase font-bold text-slate-500">Ay</span>
        <select value={month} onChange={e => setMonth(+e.target.value)}
                className="block border rounded px-2 py-1 text-sm" data-testid="vh-month">
          {MONTHS.map((m,i)=><option key={i} value={i+1}>{m}</option>)}
        </select>
      </label>
      <span className="text-[11px] text-slate-500 ml-2">Geçmiş aylar düzenlenebilir — kilitli değildir.</span>
    </div>
  );
}

/* ─── Section A — Debt Summary + CRUD ─────────────────────────────────── */
function DebtSummaryPanel({ reload, admin, onChange }) {
  const [summary, setSummary] = useState(null);
  const [open, setOpen] = useState(true);
  const [showDebts, setShowDebts] = useState(false);
  useEffect(() => {
    // iter329.6 — Combined endpoint: eski tax_debts + yeni overdue obligations
    api.get("/tax-calculation/obligations/debt-summary").then(setSummary)
       .catch(e => setSummary(null));
  }, [reload]);
  if (!summary) return null;
  const bc = summary.by_company;
  const byType = summary.by_type || {};
  const typeOrder = ["KDV", "KURUM_GECICI", "YILLIK_KURUMLAR", "MUHTASAR", "OTV", "SGK"];
  return (
    <div className="bg-white border border-slate-200 rounded-lg" data-testid="vh-debt-summary">
      <div className="flex items-center justify-between p-2 border-b">
        <button onClick={()=>setOpen(x=>!x)} className="flex items-center gap-1 text-sm font-bold text-slate-700">
          {open ? <ChevronDown size={14}/> : <ChevronRight size={14}/>}
          Vergi & SGK Borç Durumu
          <span className="text-[9px] text-slate-500 ml-2">(eski + güncel gecikmişler)</span>
        </button>
        {admin && (
          <button onClick={()=>setShowDebts(true)} data-testid="vh-manage-debts"
                   className="text-[11px] bg-indigo-600 text-white px-2 py-1 rounded flex items-center gap-1">
            <Edit3 size={10}/> Eski Borçları Yönet
          </button>
        )}
      </div>
      {open && (
        <div className="p-2 space-y-2">
          <table className="w-full text-xs">
            <thead>
              <tr className="text-left text-[10px] uppercase text-slate-500 border-b">
                <th className="py-1">Firma</th>
                <th className="text-right">Eski Borç</th>
                <th className="text-right">Vergi Ana</th>
                <th className="text-right">Vergi Gec.</th>
                <th className="text-right">SGK Ana</th>
                <th className="text-right">SGK Gec.</th>
                <th className="text-right">FİRMA TOPLAM</th>
              </tr>
            </thead>
            <tbody>
              {COMPANIES.map(c => {
                const d = bc[c] || {};
                const old = Number(d.old_debt_principal||0) + Number(d.old_debt_delay||0);
                return (
                  <tr key={c} className="border-b" data-testid={`vh-debt-row-${c}`}>
                    <td className="py-1 font-bold">{c}</td>
                    <td className="text-right font-mono text-slate-500">{money(old)}</td>
                    <td className="text-right font-mono">{money(d.new_tax_principal)}</td>
                    <td className="text-right font-mono text-rose-700">{money(d.new_tax_delay)}</td>
                    <td className="text-right font-mono">{money(d.new_sgk_principal)}</td>
                    <td className="text-right font-mono text-rose-700">{money(d.new_sgk_delay)}</td>
                    <td className="text-right font-mono font-bold">{money(d.firm_total)}</td>
                  </tr>
                );
              })}
              <tr className="font-bold bg-slate-50">
                <td className="py-1" colSpan="6">GENEL TOPLAM KAMU BORCU</td>
                <td className="text-right font-mono text-rose-800" data-testid="vh-grand-total">
                  {money(summary.grand_total_public_debt)}
                </td>
              </tr>
            </tbody>
          </table>
          {Object.keys(byType).length > 0 && (
            <div className="mt-1 border-t pt-1">
              <div className="text-[10px] uppercase font-bold text-slate-500 mb-1">Vergi Türü Bazlı Gecikmiş</div>
              <div className="grid grid-cols-3 gap-1 text-[10px]">
                {typeOrder.filter(t => byType[t]).map(t => (
                  <div key={t} className="bg-slate-50 border rounded p-1 flex justify-between">
                    <span className="font-bold">{t}</span>
                    <span className="font-mono text-rose-700">{money(byType[t].total)}</span>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      )}
      {showDebts && admin && (
        <DebtsManagerModal onClose={()=>{setShowDebts(false); onChange();}}/>
      )}
    </div>
  );
}

function DebtsManagerModal({ onClose }) {
  const [debts, setDebts] = useState([]);
  const [form, setForm] = useState({ company:"META", debt_kind:"TAX",
    sub_type:"KDV", principal:"0", due_date:new Date().toISOString().slice(0,10),
    calc_type:"NORMAL", government_amount:"", description:"" });
  const [busy, setBusy] = useState(false);
  const load = () => api.get("/tax-calculation/debts").then(setDebts);
  useEffect(() => { load(); }, []);
  const save = async () => {
    setBusy(true);
    try {
      await api.post("/tax-calculation/debts", {...form,
        government_amount: form.government_amount || null});
      setForm({...form, principal:"0", description:""});
      load();
    } catch(e) { alert(e?.response?.data?.detail || "Kayıt hatası"); }
    finally { setBusy(false); }
  };
  const close = async (did) => {
    const paid_at = prompt("Ödeme tarihi (YYYY-MM-DD):", new Date().toISOString().slice(0,10));
    if (!paid_at) return;
    await api.post(`/tax-calculation/debts/${did}/close`, { paid_at });
    load();
  };
  const del = async (did) => {
    if (!confirm("Silmek istediğinize emin misiniz?")) return;
    await api.del(`/tax-calculation/debts/${did}`);
    load();
  };
  return (
    <div className="fixed inset-0 bg-slate-900/60 z-50 flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-white rounded-lg shadow-2xl max-w-4xl w-full max-h-[90vh] overflow-auto" onClick={e=>e.stopPropagation()}>
        <div className="p-3 border-b flex justify-between items-center">
          <h3 className="font-bold">Eski Vergi / SGK Borçları</h3>
          <button onClick={onClose}><X size={16}/></button>
        </div>
        <div className="p-3 space-y-3">
          <div className="bg-slate-50 border rounded p-2 text-xs">
            <div className="grid grid-cols-6 gap-1 mb-2">
              <select value={form.company} onChange={e=>setForm({...form, company:e.target.value})} className="border rounded px-1 py-0.5 text-xs">
                {COMPANIES.map(c=><option key={c}>{c}</option>)}
              </select>
              <select value={form.debt_kind} onChange={e=>setForm({...form, debt_kind:e.target.value, calc_type:e.target.value==="SGK"?"SGK_NORMAL":"NORMAL"})} className="border rounded px-1 py-0.5 text-xs">
                <option value="TAX">Vergi</option>
                <option value="SGK">SGK</option>
              </select>
              <select value={form.sub_type} onChange={e=>setForm({...form, sub_type:e.target.value})} className="border rounded px-1 py-0.5 text-xs">
                {form.debt_kind==="TAX" ? (
                  <>
                    <option value="KDV">KDV</option>
                    <option value="KURUM">Kurumlar</option>
                    <option value="GECICI">Geçici</option>
                    <option value="STOPAJ">Stopaj/Muhtasar</option>
                    <option value="OTV">ÖTV</option>
                    <option value="DIGER">Diğer</option>
                  </>
                ) : <option value="SGK">SGK</option>}
              </select>
              <select value={form.calc_type} onChange={e=>setForm({...form, calc_type:e.target.value})} className="border rounded px-1 py-0.5 text-xs">
                {form.debt_kind==="TAX" ? (
                  <>
                    <option value="NORMAL">Normal</option>
                    <option value="DEFERMENT">Tecil</option>
                  </>
                ) : (
                  <>
                    <option value="SGK_NORMAL">Normal SGK</option>
                    <option value="SGK_DEFERRED">Tecilli SGK</option>
                  </>
                )}
              </select>
              <input placeholder="Ana borç" value={form.principal} onChange={e=>setForm({...form, principal:e.target.value})} className="border rounded px-1 py-0.5 text-xs font-mono"/>
              <input type="date" value={form.due_date} onChange={e=>setForm({...form, due_date:e.target.value})} className="border rounded px-1 py-0.5 text-xs"/>
            </div>
            <div className="grid grid-cols-6 gap-1">
              <input placeholder="Devlet Tutarı (opsiyonel)" value={form.government_amount} onChange={e=>setForm({...form, government_amount:e.target.value})} className="col-span-2 border rounded px-1 py-0.5 text-xs font-mono"/>
              <input placeholder="Açıklama" value={form.description} onChange={e=>setForm({...form, description:e.target.value})} className="col-span-3 border rounded px-1 py-0.5 text-xs"/>
              <button onClick={save} disabled={busy} className="bg-indigo-600 text-white rounded px-2 py-0.5 text-xs flex items-center justify-center gap-1 disabled:opacity-50">
                {busy ? <Loader2 size={11} className="animate-spin"/> : <Plus size={11}/>} Ekle
              </button>
            </div>
          </div>
          <table className="w-full text-xs">
            <thead>
              <tr className="text-left text-[10px] uppercase text-slate-500 border-b">
                <th>Firma</th><th>Tür</th><th>Alt Tür</th><th>Ana</th><th>Vade</th><th>Gec. Gün</th><th>Faiz</th><th>Toplam</th><th>Devlet</th><th>Fark</th><th>Durum</th><th></th>
              </tr>
            </thead>
            <tbody>
              {debts.map(d => {
                const delay = d.delay || {};
                const total = Number(delay.total || d.principal);
                const gov = d.government_amount ? Number(d.government_amount) : null;
                const diff = gov !== null ? total - gov : null;
                return (
                  <tr key={d.id} className="border-b" data-testid={`vh-debt-${d.id}`}>
                    <td>{d.company}</td>
                    <td>{d.debt_kind}</td>
                    <td className="text-[10px]">{d.sub_type}</td>
                    <td className="font-mono">{money(d.principal)}</td>
                    <td className="font-mono text-[10px]">{d.due_date}</td>
                    <td className="font-mono text-[10px]">{delay.days}</td>
                    <td className="font-mono text-rose-700">{money(delay.interest || delay.total_interest)}</td>
                    <td className="font-mono font-bold">{money(total)}</td>
                    <td className="font-mono text-[10px]">{gov !== null ? money(gov) : "—"}</td>
                    <td className={`font-mono text-[10px] ${diff > 0 ? "text-rose-700" : "text-emerald-700"}`}>
                      {diff !== null ? (diff > 0 ? "+" : "") + money(diff) : "—"}
                    </td>
                    <td>
                      {d.status === "CLOSED" ? <span className="text-emerald-700 text-[10px]">Kapalı ({d.paid_at})</span> : <span className="text-rose-700 text-[10px]">Açık</span>}
                    </td>
                    <td>
                      {d.status !== "CLOSED" && (
                        <button onClick={()=>close(d.id)} className="text-[10px] text-emerald-700 hover:underline" data-testid={`vh-debt-close-${d.id}`}>
                          <CheckCircle size={10} className="inline"/> Kapat
                        </button>
                      )}
                      <button onClick={()=>del(d.id)} className="text-[10px] text-rose-700 hover:underline ml-1">
                        <Trash2 size={10} className="inline"/>
                      </button>
                    </td>
                  </tr>
                );
              })}
              {debts.length === 0 && (
                <tr><td colSpan="12" className="text-center py-2 text-slate-400">Kayıt yok</td></tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

/* ─── Section B — Rates Panel ─────────────────────────────────────────── */
const RATE_LABELS = {
  "KDV": "KDV", "KURUM": "Kurum/Geçici Vergi", "KIRA_STOPAJ": "Kira Stopajı",
  "PERSONEL_STOPAJ": "Personel Stopajı (ortalama)", "SGK_EMPLOYEE": "SGK Çalışan",
  "SGK_EMPLOYER": "SGK İşveren", "VERGI_GECIKME_AYLIK": "Vergi Gecikme Zammı (aylık)",
  "TECIL_YILLIK": "Tecil Faizi (yıllık)", "SGK_ILK3AY_AYLIK": "SGK İlk 3 Ay (aylık)",
};

function RatesPanel({ admin }) {
  const [open, setOpen] = useState(false);
  const [rates, setRates] = useState([]);
  const [form, setForm] = useState({ rate_type:"KDV", value:"20",
    effective_from: new Date().toISOString().slice(0,10) });
  const load = () => api.get("/tax-calculation/rates").then(setRates);
  useEffect(() => { load(); }, []);
  const add = async () => {
    try {
      await api.post("/tax-calculation/rates", { ...form, unit: "pct" });
      load();
    } catch(e) { alert(e?.response?.data?.detail || "Oran kaydı başarısız"); }
  };
  const grouped = useMemo(() => {
    const g = {};
    rates.forEach(r => { (g[r.rate_type] = g[r.rate_type] || []).push(r); });
    return g;
  }, [rates]);
  return (
    <div className="bg-white border rounded-lg">
      <button onClick={()=>setOpen(x=>!x)} className="w-full flex items-center gap-1 p-2 text-sm font-bold text-slate-700 border-b">
        {open ? <ChevronDown size={14}/> : <ChevronRight size={14}/>}
        Hesaplama Oranları (Versioned)
      </button>
      {open && (
        <div className="p-2 space-y-2 text-xs">
          {admin && (
            <div className="bg-slate-50 rounded p-2 flex gap-1 items-center">
              <select value={form.rate_type} onChange={e=>setForm({...form, rate_type:e.target.value})} className="border rounded px-1 py-0.5">
                {Object.entries(RATE_LABELS).map(([k,l])=><option key={k} value={k}>{l}</option>)}
              </select>
              <input placeholder="Değer %" value={form.value} onChange={e=>setForm({...form, value:e.target.value})} className="border rounded px-1 py-0.5 w-24 font-mono"/>
              <input type="date" value={form.effective_from} onChange={e=>setForm({...form, effective_from:e.target.value})} className="border rounded px-1 py-0.5"/>
              <button onClick={add} className="bg-indigo-600 text-white rounded px-2 py-0.5 text-xs" data-testid="vh-rate-add">
                <Plus size={10} className="inline"/> Yeni Oran
              </button>
            </div>
          )}
          <table className="w-full">
            <thead>
              <tr className="text-left text-[10px] uppercase text-slate-500 border-b">
                <th>Oran Türü</th><th className="text-right">Değer</th><th>Yürürlük</th>
              </tr>
            </thead>
            <tbody>
              {Object.entries(grouped).map(([k,arr]) =>
                arr.map((r, i) => (
                  <tr key={r.id} className={i === 0 ? "font-bold border-b" : "border-b text-slate-500"}>
                    <td>{i === 0 ? (RATE_LABELS[k] || k) : <span className="text-[10px] pl-3">(geçmiş)</span>}</td>
                    <td className="text-right font-mono">%{r.value}</td>
                    <td className="font-mono text-[10px]">{r.effective_from}</td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

function DibsPanel({ admin }) {
  const [open, setOpen] = useState(false);
  const [dibs, setDibs] = useState([]);
  const [form, setForm] = useState({
    year: new Date().getFullYear(), month: 1, value: "2.5"
  });
  const load = () => api.get("/tax-calculation/dibs").then(setDibs);
  useEffect(() => { load(); }, []);
  const add = async () => {
    try {
      await api.post("/tax-calculation/dibs", { year: +form.year, month: +form.month, value: form.value });
      load();
    } catch(e) { alert(e?.response?.data?.detail || "DİBS kaydı başarısız"); }
  };
  return (
    <div className="bg-white border rounded-lg">
      <button onClick={()=>setOpen(x=>!x)} className="w-full flex items-center gap-1 p-2 text-sm font-bold text-slate-700 border-b">
        {open ? <ChevronDown size={14}/> : <ChevronRight size={14}/>}
        SGK DİBS Aylık Oranları
      </button>
      {open && (
        <div className="p-2 text-xs">
          {admin && (
            <div className="flex gap-1 mb-2 bg-slate-50 p-2 rounded">
              <input type="number" value={form.year} onChange={e=>setForm({...form,year:e.target.value})} className="border rounded px-1 py-0.5 w-20"/>
              <select value={form.month} onChange={e=>setForm({...form,month:e.target.value})} className="border rounded px-1 py-0.5">
                {MONTHS.map((m,i)=><option key={i} value={i+1}>{m}</option>)}
              </select>
              <input value={form.value} onChange={e=>setForm({...form,value:e.target.value})} placeholder="Aylık %" className="border rounded px-1 py-0.5 w-20 font-mono"/>
              <button onClick={add} className="bg-indigo-600 text-white rounded px-2 py-0.5" data-testid="vh-dibs-add">
                <Plus size={10} className="inline"/> Kaydet
              </button>
            </div>
          )}
          <div className="grid grid-cols-6 gap-1 text-[10px]">
            {dibs.map(d => (
              <div key={`${d.year}-${d.month}`} className="border rounded p-1 flex justify-between">
                <span className="font-mono">{d.year}/{String(d.month).padStart(2,"0")}</span>
                <span className="font-mono font-bold">%{d.value}</span>
              </div>
            ))}
            {dibs.length === 0 && <div className="col-span-6 text-slate-400 text-center py-2">Kayıt yok</div>}
          </div>
        </div>
      )}
    </div>
  );
}

/* ─── Sections D/E/F — Company Block (Opening + Monthly + History) ──── */
function CompanyBlock({ company, year, month, edit, admin }) {
  const isMeta = company === "META";
  // iter329.5 — yıl bazlı opening + obligations state
  const [openingYear, setOpeningYear] = useState(year);
  const [opening, setOpening] = useState({
    opening_vat_carry:"0",
    opening_cumulative_profit_loss:"0",
    opening_provisional_tax_paid_ytd:"0",
    opening_raw_inventory:"0",
  });
  const [obligations, setObligations] = useState([]);
  const [showObligModal, setShowObligModal] = useState(null); // {quarter, calc}
  const [inputs, setInputs] = useState({ gross_sales:"0", gross_purchases:"0",
    vat_rate_pct:"20", excise_tax:"0", closing_raw_inventory:"0", general_expenses:[] });
  const [computed, setComputed] = useState(null);
  const [overrides, setOverrides] = useState({});
  const [busy, setBusy] = useState(false);
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState("");
  const [recordId, setRecordId] = useState(null);
  const [history, setHistory] = useState([]);
  const [showHist, setShowHist] = useState(false);
  const [showExp, setShowExp] = useState(true);
  const [overrideField, setOverrideField] = useState(null);  // {field, label, calculated}

  // Load persisted month + opening + history + obligations
  useEffect(() => { setOpeningYear(year); }, [year]);
  useEffect(() => {
    let alive = true;
    api.get(`/tax-calculation/openings/${company}?year=${openingYear}`)
       .then(o => alive && setOpening({
         opening_vat_carry: o.opening_vat_carry || "0",
         opening_cumulative_profit_loss:
           o.opening_cumulative_profit_loss || o.opening_cumulative_profit || "0",
         opening_provisional_tax_paid_ytd: o.opening_provisional_tax_paid_ytd || "0",
         opening_raw_inventory: o.opening_raw_inventory || "0",
       })).catch(()=>{});
    // Load KURUM_GECICI obligations for this company + tax year
    api.get(`/tax-calculation/obligations?company=${company}&tax_year=${year}&obligation_type=KURUM_GECICI`)
       .then(rows => alive && setObligations(rows || [])).catch(()=>{});
    api.get(`/tax-calculation/months?company=${company}&year=${year}`).then(rows => {
      if (!alive) return;
      setHistory(rows || []);
      const rec = (rows || []).find(r => r.month === month);
      if (rec?.inputs) {
        setRecordId(rec.id);
        setInputs({
          gross_sales: rec.inputs.gross_sales || "0",
          gross_purchases: rec.inputs.gross_purchases || "0",
          vat_rate_pct: rec.inputs.vat_rate_pct || "20",
          excise_tax: rec.inputs.excise_tax || "0",
          closing_raw_inventory: rec.inputs.closing_raw_inventory || "0",
          general_expenses: rec.inputs.general_expenses || [],
        });
        setOverrides(rec.manual_overrides || {});
        setComputed(rec.computed);
      } else {
        setRecordId(null);
        setInputs({ gross_sales:"0", gross_purchases:"0", vat_rate_pct:"20",
          excise_tax:"0", closing_raw_inventory:"0", general_expenses:[] });
        setOverrides({});
        setComputed(null);
      }
    }).catch(e => alive && setErr(e?.message || "Yüklenemedi"));
    return () => { alive = false; };
  }, [company, year, month, openingYear]);

  // Live calc (debounced)
  useEffect(() => {
    const t = setTimeout(async () => {
      setBusy(true); setErr("");
      try {
        const r = await api.post("/tax-calculation/calculate",
          { company, year, month, ...inputs });
        setComputed(r);
      } catch(e) {
        setErr(e?.response?.data?.detail || "Hesaplama hatası");
      } finally { setBusy(false); }
    }, 400);
    return () => clearTimeout(t);
    // eslint-disable-next-line
  }, [company, year, month, inputs.gross_sales, inputs.gross_purchases,
      inputs.vat_rate_pct, inputs.excise_tax, inputs.closing_raw_inventory,
      JSON.stringify(inputs.general_expenses)]);

  const saveOpening = async () => {
    try {
      const r = await api.put(`/tax-calculation/openings/${company}`,
        { company, tax_year: openingYear, ...opening });
      setOpening({
        opening_vat_carry: r.opening_vat_carry || "0",
        opening_cumulative_profit_loss:
          r.opening_cumulative_profit_loss || "0",
        opening_provisional_tax_paid_ytd:
          r.opening_provisional_tax_paid_ytd || "0",
        opening_raw_inventory: r.opening_raw_inventory || "0",
      });
    } catch(e) { alert(e?.response?.data?.detail || "Opening kayıt başarısız"); }
  };
  // iter329.5 — Record actual-paid provisional tax for a quarter
  const payProvisional = async (quarter, amount, payment_date, note, calculated) => {
    const existing = obligations.find(o => o.quarter === quarter);
    if (existing) {
      await api.post(`/tax-calculation/obligations/${existing.id}/pay`,
        { actual_paid_amount: amount, payment_date, note: note || null });
    } else {
      await api.post(`/tax-calculation/obligations`, {
        company, tax_year: year,
        period_year: year,
        period_month: ({Q1:3,Q2:6,Q3:9,Q4:12})[quarter],
        quarter, obligation_type: "KURUM_GECICI",
        calculated_amount: calculated || "0",
        payment_status: "PAID",
        actual_paid_amount: amount,
        payment_date, note,
      });
    }
    // Refresh obligations + computed
    const rows = await api.get(
      `/tax-calculation/obligations?company=${company}&tax_year=${year}&obligation_type=KURUM_GECICI`);
    setObligations(rows || []);
    // Trigger recompute of current month
    try {
      const r = await api.post("/tax-calculation/calculate",
        { company, year, month, ...inputs });
      setComputed(r);
    } catch(_){}
  };
  const deleteProvisional = async (oid) => {
    if (!confirm("Bu dönem ödeme kaydını silmek istiyor musunuz?")) return;
    await api.del(`/tax-calculation/obligations/${oid}`);
    const rows = await api.get(
      `/tax-calculation/obligations?company=${company}&tax_year=${year}&obligation_type=KURUM_GECICI`);
    setObligations(rows || []);
    try {
      const r = await api.post("/tax-calculation/calculate",
        { company, year, month, ...inputs });
      setComputed(r);
    } catch(_){}
  };

  const save = async () => {
    setSaving(true); setErr("");
    try {
      const r = await api.post("/tax-calculation/months", { company, year, month, ...inputs });
      setRecordId(r.id);
      setComputed(r.computed);
      const rows = await api.get(`/tax-calculation/months?company=${company}&year=${year}`);
      setHistory(rows || []);
    } catch(e) { setErr(e?.response?.data?.detail || "Kayıt başarısız"); }
    finally { setSaving(false); }
  };

  // iter329.3 — Manual override helpers
  const applyOverride = async (field, manual_value, reason) => {
    if (!recordId) { alert("Önce bu ayı kaydedin"); return; }
    try {
      const r = await api.post(`/tax-calculation/months/${recordId}/override`,
        { field, manual_value, reason });
      setOverrides(r.manual_overrides || {});
    } catch(e) { alert(e?.response?.data?.detail || "Override başarısız"); }
  };
  const removeOverride = (field) => applyOverride(field, null, null);
  const effVal = (field, calcVal) => {
    const ov = overrides[field];
    if (ov && ov.manual_value != null && ov.manual_value !== "") return ov.manual_value;
    return calcVal;
  };
  // iter329.5 — Aylık kamu yükü: geçici vergi ÇIKARTILDI (dönem yükü ayrı)
  const burdenEffective = useMemo(() => {
    if (!computed) return "0";
    const vat = Number(effVal("payable_vat", computed.vat?.payable_vat) || 0);
    const rent = Number(effVal("rent_withholding_total", computed.expenses?.rent_withholding_total) || 0);
    const pers = Number(effVal("personnel_tax_total", computed.expenses?.personnel_tax_total) || 0);
    const exc = isMeta ? Number(effVal("excise_tax", computed.excise_tax) || 0) : 0;
    const sgk = Number(effVal("sgk_total", computed.sgk?.total) || 0);
    return (vat + rent + pers + exc + sgk).toFixed(2);
  }, [computed, overrides, isMeta]);

  const addExp = (kind) => {
    setInputs(s => ({...s, general_expenses: [...(s.general_expenses||[]),
      { kind, amount:"0", description:"",
        rent_withholding_subject: kind==="KIRA" ? true : undefined }]}));
  };
  const updExp = (i, patch) => {
    setInputs(s => {
      const g = [...(s.general_expenses||[])];
      g[i] = { ...g[i], ...patch };
      return { ...s, general_expenses: g };
    });
  };
  const delExp = (i) => {
    setInputs(s => ({ ...s, general_expenses: (s.general_expenses||[]).filter((_,j)=>j!==i) }));
  };

  const getVal = (field, calc) => {
    if (overrides[field]) return overrides[field].manual_value;
    return calc;
  };

  return (
    <div className="bg-white border border-slate-200 rounded-lg p-3 space-y-2 text-xs" data-testid={`vh-card-${company}`}>
      <div className="flex items-center justify-between pb-2 border-b">
        <h3 className="font-bold text-sm text-indigo-800">{company}</h3>
        {edit && (
          <button onClick={save} disabled={saving} className="text-[11px] bg-indigo-600 text-white px-2 py-1 rounded flex items-center gap-1 disabled:opacity-50" data-testid={`vh-save-${company}`}>
            {saving ? <Loader2 size={11} className="animate-spin"/> : <Save size={11}/>} Kaydet
          </button>
        )}
      </div>

      {/* iter329.5 — Opening/Devir: yıl bazlı + 2 blok (KDV / Kurum) */}
      <details className="bg-slate-50 rounded p-1" open={false}>
        <summary className="cursor-pointer text-[10px] uppercase font-bold text-slate-500">Başlangıç / Devir (yıl bazlı)</summary>
        <div className="pt-1 space-y-2">
          <label className="block">
            <span className="text-[9px] uppercase font-bold text-slate-500">Vergi Yılı</span>
            <select value={openingYear} onChange={e=>setOpeningYear(+e.target.value)}
                    className="w-full border rounded px-2 py-0.5 text-[11px]"
                    data-testid={`vh-op-year-${company}`}>
              {Array.from({length:8},(_,i)=>new Date().getFullYear()-3+i).map(y=>
                <option key={y} value={y}>{y}</option>)}
            </select>
          </label>
          <div className="border-l-2 border-indigo-300 pl-2">
            <div className="text-[9px] uppercase font-bold text-indigo-700">KDV Başlangıç (sürekli)</div>
            <Field tiny label="Devreden KDV" value={opening.opening_vat_carry}
                    onChange={v=>setOpening({...opening,opening_vat_carry:v})}
                    testid={`vh-op-vat-${company}`}/>
          </div>
          <div className="border-l-2 border-rose-300 pl-2">
            <div className="text-[9px] uppercase font-bold text-rose-700">Kurum / Geçici Vergi (yıl bazlı — reset)</div>
            <Field tiny label="Yıl Başından Bugüne Kâr/Zarar"
                    value={opening.opening_cumulative_profit_loss}
                    onChange={v=>setOpening({...opening,opening_cumulative_profit_loss:v})}
                    testid={`vh-op-cum-${company}`}/>
            <Field tiny label="Bu Yıl Daha Önce Ödenmiş Geçici Vergi"
                    value={opening.opening_provisional_tax_paid_ytd}
                    onChange={v=>setOpening({...opening,opening_provisional_tax_paid_ytd:v})}
                    testid={`vh-op-prov-${company}`}/>
          </div>
          {isMeta && (
            <div className="border-l-2 border-amber-300 pl-2">
              <div className="text-[9px] uppercase font-bold text-amber-700">META Stok (sürekli)</div>
              <Field tiny label="Başlangıç Hammadde Stok" value={opening.opening_raw_inventory}
                      onChange={v=>setOpening({...opening,opening_raw_inventory:v})}
                      testid={`vh-op-inv-${company}`}/>
            </div>
          )}
          {edit && <button onClick={saveOpening} data-testid={`vh-op-save-${company}`}
                             className="text-[10px] bg-slate-700 text-white rounded px-2 py-0.5">
                     Opening Kaydet ({openingYear})
                   </button>}
        </div>
      </details>

      {/* Inputs */}
      <Field label="KDV Dahil Satış" value={inputs.gross_sales}
              onChange={v=>setInputs({...inputs,gross_sales:v})} testid={`vh-sales-${company}`}/>
      <Field label="KDV Dahil Alış" value={inputs.gross_purchases}
              onChange={v=>setInputs({...inputs,gross_purchases:v})} testid={`vh-purchases-${company}`}/>
      <Field label="KDV Oranı %" value={inputs.vat_rate_pct}
              onChange={v=>setInputs({...inputs,vat_rate_pct:v})} testid={`vh-vat-${company}`}/>
      {isMeta && (
        <>
          <Field label="ÖTV" value={inputs.excise_tax}
                  onChange={v=>setInputs({...inputs,excise_tax:v})} testid={`vh-excise-${company}`}/>
          <Field label="Güncel Hammadde Stok" value={inputs.closing_raw_inventory}
                  onChange={v=>setInputs({...inputs,closing_raw_inventory:v})} testid={`vh-stock-${company}`}/>
          <div className="text-[10px] text-slate-500">Manuel değerdir — envanteri etkilemez.</div>
        </>
      )}

      {/* Expenses */}
      <div className="border-t pt-1">
        <button onClick={()=>setShowExp(x=>!x)} className="text-[10px] uppercase font-bold text-slate-500 flex items-center gap-1">
          {showExp ? <ChevronDown size={10}/> : <ChevronRight size={10}/>} Genel Giderler ({(inputs.general_expenses||[]).length})
        </button>
        {showExp && (
          <div className="pt-1 space-y-1">
            {(inputs.general_expenses||[]).map((e, i) => (
              <div key={i} className="bg-slate-50 rounded p-1 flex gap-1 items-center" data-testid={`vh-exp-${company}-${i}`}>
                <select value={e.kind} onChange={ev=>updExp(i,{kind:ev.target.value})} className="border rounded text-[10px] px-0.5">
                  <option value="PERSONEL_BRUT">Personel Brüt</option>
                  <option value="KIRA">Kira</option>
                  <option value="DIGER">Diğer</option>
                </select>
                <input value={e.amount} onChange={ev=>updExp(i,{amount:ev.target.value})} className="border rounded text-[10px] px-0.5 w-20 font-mono"/>
                {e.kind === "KIRA" && (
                  <label className="text-[10px] flex items-center gap-0.5">
                    <input type="checkbox" checked={!!e.rent_withholding_subject} onChange={ev=>updExp(i,{rent_withholding_subject:ev.target.checked})}/>
                    Stop.
                  </label>
                )}
                <input value={e.description || ""} onChange={ev=>updExp(i,{description:ev.target.value})} placeholder="açıklama" className="border rounded text-[10px] px-0.5 flex-1"/>
                <button onClick={()=>delExp(i)} className="text-rose-600"><Trash2 size={10}/></button>
              </div>
            ))}
            {edit && (
              <div className="flex gap-1 text-[10px]">
                <button onClick={()=>addExp("PERSONEL_BRUT")} className="bg-slate-200 rounded px-1 py-0.5">+ Personel</button>
                <button onClick={()=>addExp("KIRA")} className="bg-slate-200 rounded px-1 py-0.5">+ Kira</button>
                <button onClick={()=>addExp("DIGER")} className="bg-slate-200 rounded px-1 py-0.5">+ Diğer</button>
              </div>
            )}
            <div className="text-[9px] text-amber-700">Alış faturalarına dahil giderleri tekrar girmeyin.</div>
          </div>
        )}
      </div>

      {err && <div className="bg-red-50 border border-red-200 rounded px-2 py-1 text-[10px] text-red-700">{err}</div>}

      {/* iter329.5 — Computed display grouped: KDV / Kurum-Geçici / Diğer */}
      {computed && (
        <div className="pt-2 border-t space-y-1 bg-slate-50 -mx-3 -mb-3 px-3 pb-3 rounded-b">
          {/* ─── KDV Grubu ─── */}
          <div className="border-l-2 border-indigo-400 pl-2 py-1" data-testid={`vh-group-vat-${company}`}>
            <div className="text-[9px] uppercase font-bold text-indigo-700 mb-0.5">KDV</div>
            <Row k="Net Satış" v={computed.sales?.net}/>
            <Row k="Net Alış" v={computed.purchases?.net}/>
            <Row k="Satış KDV" v={computed.vat?.sales_vat}/>
            <Row k="Alış KDV" v={computed.vat?.purchase_vat}/>
            <Row k="Önceki Devreden KDV" v={computed.vat?.prev_carry}/>
            <OverrideRow k="Bu Ay Ödenecek KDV" field="payable_vat" calc={computed.vat?.payable_vat}
                          overrides={overrides} admin={admin} recordId={recordId}
                          onEdit={() => setOverrideField({field:"payable_vat", label:"Ödenecek KDV", calculated: computed.vat?.payable_vat})}
                          onRemove={() => removeOverride("payable_vat")} bold/>
            <Row k="Sonraki Aya Devreden KDV" v={computed.vat?.new_carry}/>
          </div>

          {/* ─── Kurum / Geçici Vergi Grubu ─── */}
          <div className="border-l-2 border-rose-400 pl-2 py-1" data-testid={`vh-group-corp-${company}`}>
            <div className="text-[9px] uppercase font-bold text-rose-700 mb-0.5">
              Kurum / Geçici Vergi — Dönem: {computed.quarter || "-"}
            </div>
            {isMeta && (
              <>
                <Row k="Dönem Başı Stok" v={computed.profit?.inv_open}/>
                <Row k="Güncel Stok" v={computed.profit?.inv_close}/>
                <Row k="Stok Değişimi" v={computed.profit?.inv_change}/>
              </>
            )}
            <Row k="Bu Ay Tahmini Kâr/Zarar" v={computed.profit?.profit_this_month} bold/>
            <Row k="Yıl Başından Kümülatif Kâr/Zarar"
                  v={computed.provisional_period?.ytd_cumulative_profit}/>
            <Row k="Kümülatif Hesaplanan Geçici Vergi"
                  v={computed.provisional_period?.cumulative_calculated_tax}/>
            <Row k="Önceki Dönemlerde Ödenmiş Geçici Vergi"
                  v={computed.provisional_period?.previous_provisional_paid_ytd}/>
            <Row k={`${computed.quarter || "-"} Dönem Sonu Bugünkü Verilerle Tahmini Geçici Vergi`}
                  v={computed.provisional_period?.estimated_period_provisional_tax} bold/>
            <Row k="Bu Dönem Gerçekte Ödenen"
                  v={computed.provisional_period?.actual_paid_this_quarter}/>
            <Row k="Yıllık Tahmini Kurumlar Vergisi"
                  v={computed.annual_corp_forecast?.annual_estimated_corp_tax}/>
            <Row k="Yıllık Kalan (Gerçek Ödenen Düşülünce)"
                  v={computed.annual_corp_forecast?.annual_remaining_actual_only}/>
            <div className="text-[9px] text-slate-500 italic mt-0.5">
              {computed.provisional_period?.note}
            </div>
            {edit && (
              <button onClick={()=>setShowObligModal({
                  quarter: computed.quarter,
                  calculated: computed.provisional_period?.estimated_period_provisional_tax,
                })}
                className="mt-1 text-[10px] bg-rose-600 text-white rounded px-2 py-0.5"
                data-testid={`vh-pay-prov-${company}`}>
                Gerçekte Ödenen Geçici Vergi Gir ({computed.quarter})
              </button>
            )}
          </div>

          {/* ─── Diğer: Giderler + Stopajlar + SGK + ÖTV ─── */}
          <div className="border-l-2 border-slate-400 pl-2 py-1" data-testid={`vh-group-other-${company}`}>
            <div className="text-[9px] uppercase font-bold text-slate-700 mb-0.5">Giderler, Stopaj, SGK</div>
            <Row k="Toplam Gider" v={computed.expenses?.total}/>
            <OverrideRow k="Kira Stopajı" field="rent_withholding_total" calc={computed.expenses?.rent_withholding_total}
                          overrides={overrides} admin={admin} recordId={recordId}
                          onEdit={() => setOverrideField({field:"rent_withholding_total", label:"Kira Stopajı", calculated: computed.expenses?.rent_withholding_total})}
                          onRemove={() => removeOverride("rent_withholding_total")}/>
            <OverrideRow k="Personel Vergi" field="personnel_tax_total" calc={computed.expenses?.personnel_tax_total}
                          overrides={overrides} admin={admin} recordId={recordId}
                          onEdit={() => setOverrideField({field:"personnel_tax_total", label:"Personel Vergi", calculated: computed.expenses?.personnel_tax_total})}
                          onRemove={() => removeOverride("personnel_tax_total")}/>
            <Row k="Muhtasar" v={computed.expenses?.muhtasar}/>
            {isMeta && <Row k="ÖTV" v={computed.excise_tax}/>}
            <OverrideRow k="SGK Toplam" field="sgk_total" calc={computed.sgk?.total}
                          overrides={overrides} admin={admin} recordId={recordId}
                          onEdit={() => setOverrideField({field:"sgk_total", label:"SGK Toplam", calculated: computed.sgk?.total})}
                          onRemove={() => removeOverride("sgk_total")}/>
          </div>

          <div className="mt-1 pt-1 border-t flex justify-between font-bold text-rose-800">
            <span>Bu Ay Tahmini Kamu Yükü (KDV+Stopaj+SGK+ÖTV)</span>
            <span className="font-mono" data-testid={`vh-burden-${company}`}>{money(burdenEffective)}</span>
          </div>
          <div className="text-[9px] text-slate-500 italic">
            Not: Geçici vergi aylık ödeme değildir — yukarıdaki kamu yüküne eklenmez.
            Yalnız dönem sonunda ödenir ve ayrı takip edilir.
          </div>
          {busy && <div className="text-[9px] text-slate-400">Yeniden hesaplanıyor…</div>}
        </div>
      )}

      {/* iter329.5 — Provisional Paid Modal */}
      {showObligModal && (
        <ProvisionalPayModal
          quarter={showObligModal.quarter}
          calculated={showObligModal.calculated}
          existing={obligations.find(o => o.quarter === showObligModal.quarter)}
          onClose={() => setShowObligModal(null)}
          onSave={async (amount, payment_date, note) => {
            await payProvisional(showObligModal.quarter, amount, payment_date, note,
                                   showObligModal.calculated);
            setShowObligModal(null);
          }}
          onDelete={async () => {
            const ex = obligations.find(o => o.quarter === showObligModal.quarter);
            if (ex) await deleteProvisional(ex.id);
            setShowObligModal(null);
          }}
        />
      )}

      {overrideField && (
        <OverrideModal
          field={overrideField}
          existing={overrides[overrideField.field]}
          onClose={() => setOverrideField(null)}
          onSave={async (val, reason) => {
            await applyOverride(overrideField.field, val, reason);
            setOverrideField(null);
          }}
        />
      )}

      {/* History */}
      <div className="pt-1 border-t">
        <button onClick={()=>setShowHist(x=>!x)} className="text-[10px] uppercase font-bold text-slate-500 flex items-center gap-1">
          {showHist ? <ChevronDown size={10}/> : <ChevronRight size={10}/>} Geçmiş Aylar ({history.length})
        </button>
        {showHist && (
          <table className="w-full text-[10px] mt-1">
            <thead>
              <tr className="text-left text-slate-500 border-b">
                <th>Ay</th>
                <th className="text-right">Öd. KDV</th>
                <th className="text-right">Devreden</th>
                <th className="text-right">Aylık Kâr</th>
                <th className="text-right">YTD Kâr</th>
                <th className="text-center">Dönem</th>
                <th className="text-right">Dönem Tahmini</th>
                <th className="text-right">Yük</th>
              </tr>
            </thead>
            <tbody>
              {history.sort((a,b)=>a.month-b.month).map(h => (
                <tr key={h.id} className="border-b">
                  <td>{MONTHS[h.month-1]}</td>
                  <td className="text-right font-mono">{money(h.computed?.vat?.payable_vat)}</td>
                  <td className="text-right font-mono text-slate-500">{money(h.computed?.vat?.new_carry)}</td>
                  <td className="text-right font-mono">{money(h.computed?.profit?.profit_this_month)}</td>
                  <td className="text-right font-mono">{money(h.computed?.profit?.cumulative_profit)}</td>
                  <td className="text-center text-[9px]">{h.computed?.quarter || "-"}</td>
                  <td className="text-right font-mono text-rose-700">{money(h.computed?.provisional_period?.estimated_period_provisional_tax)}</td>
                  <td className="text-right font-mono font-bold">{money(h.computed?.public_burden_this_month)}</td>
                </tr>
              ))}
              {history.length === 0 && <tr><td colSpan="8" className="text-center py-1 text-slate-400">Kayıt yok</td></tr>}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}

/* ─── Section I — Monthly Summary ─────────────────────────────────────── */
function MonthlySummaryPanel({ year, month }) {
  const [s, setS] = useState(null);
  useEffect(() => {
    api.get(`/tax-calculation/monthly-summary?year=${year}&month=${month}`)
       .then(setS).catch(()=>setS(null));
  }, [year, month]);
  if (!s) return null;
  return (
    <div className="bg-indigo-50 border border-indigo-300 rounded-lg p-3 text-xs" data-testid="vh-monthly-summary">
      <h3 className="font-bold text-sm text-indigo-900 mb-2">
        {MONTHS[month-1]} {year} — Aylık Genel Özet
      </h3>
      <div className="grid grid-cols-5 gap-2 text-[11px]">
        {s.rows.map(r => (
          <div key={r.company} className="bg-white rounded p-2 text-center" data-testid={`vh-sum-${r.company}`}>
            <div className="font-bold text-slate-700">{r.company}</div>
            <div className="text-[9px] text-slate-400">Aylık Yük</div>
            <div className="font-mono text-rose-700">{money(r.burden)}</div>
            {r.has_data && (
              <>
                <div className="text-[9px] text-slate-400 mt-0.5">{r.quarter || "-"} Tahmini</div>
                <div className="font-mono text-amber-700 text-[10px]">{money(r.provisional_forecast)}</div>
              </>
            )}
          </div>
        ))}
        <div className="bg-indigo-900 text-white rounded p-2 text-center col-span-2">
          <div className="text-[10px]">Toplam Aylık Kamu Yükü</div>
          <div className="font-mono font-bold text-sm" data-testid="vh-grand-month">
            {money(s.totals.total_burden_this_month)}
          </div>
        </div>
      </div>
      <div className="grid grid-cols-3 gap-2 text-[11px] mt-2">
        <div>Vergi (KDV+Stopaj+ÖTV): <b className="font-mono">{money(s.totals.total_tax)}</b></div>
        <div>SGK: <b className="font-mono">{money(s.totals.total_sgk)}</b></div>
        <div className="text-amber-800">Yaklaşan Geçici Vergi (dönem sonu):
          <b className="font-mono"> {money(s.totals.total_provisional_forecast)}</b></div>
      </div>
      <div className="mt-1 pt-1 border-t text-[10px] text-slate-600">
        Aylık yük + Yaklaşan Geçici Vergi:
        <b className="font-mono text-slate-900"> {money(s.totals.total_burden_plus_provisional)}</b>
        <span className="italic ml-2">(bilgi amaçlı — geçici vergi aylık değildir)</span>
      </div>
    </div>
  );
}

/* iter329.5 — Provisional Paid Modal */
function ProvisionalPayModal({ quarter, calculated, existing, onClose, onSave, onDelete }) {
  const [amount, setAmount] = useState(existing?.actual_paid_amount || calculated || "0");
  const [paymentDate, setPaymentDate] = useState(
    existing?.payment_date || new Date().toISOString().slice(0,10));
  const [note, setNote] = useState(existing?.note || "");
  const [busy, setBusy] = useState(false);
  return (
    <div className="fixed inset-0 bg-slate-900/60 z-50 flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-white rounded-lg shadow-2xl max-w-md w-full" onClick={e=>e.stopPropagation()} data-testid="vh-pay-modal">
        <div className="p-3 border-b flex justify-between items-center">
          <h3 className="font-bold text-sm">Gerçekte Ödenen Geçici Vergi — {quarter}</h3>
          <button onClick={onClose}><X size={16}/></button>
        </div>
        <div className="p-3 space-y-2 text-xs">
          <div className="bg-slate-50 border rounded p-2">
            <div className="text-[10px] uppercase font-bold text-slate-500">Hesaplanan Tahmini</div>
            <div className="font-mono text-sm">{money(calculated)}</div>
          </div>
          <label className="block">
            <span className="text-[10px] uppercase font-bold text-slate-500">Gerçekte Ödenen Tutar *</span>
            <input value={amount} onChange={e=>setAmount(e.target.value)} inputMode="decimal"
                    className="w-full border rounded px-2 py-1 text-sm font-mono"
                    data-testid="vh-pay-amount"/>
          </label>
          <label className="block">
            <span className="text-[10px] uppercase font-bold text-slate-500">Ödeme Tarihi *</span>
            <input type="date" value={paymentDate} onChange={e=>setPaymentDate(e.target.value)}
                    className="w-full border rounded px-2 py-1 text-xs font-mono"
                    data-testid="vh-pay-date"/>
          </label>
          <label className="block">
            <span className="text-[10px] uppercase font-bold text-slate-500">Not</span>
            <textarea value={note} onChange={e=>setNote(e.target.value)} rows={2}
                       className="w-full border rounded px-2 py-1 text-xs"/>
          </label>
          <div className="flex justify-between gap-2 pt-2 border-t">
            {existing && (
              <button onClick={onDelete} className="text-xs px-2 py-1 bg-rose-100 text-rose-700 rounded">
                Kaydı Sil
              </button>
            )}
            <div className="flex gap-2 ml-auto">
              <button onClick={onClose} className="text-xs px-2 py-1 bg-slate-200 rounded">Vazgeç</button>
              <button disabled={busy || !amount || !paymentDate} onClick={async()=>{
                setBusy(true);
                await onSave(amount, paymentDate, note);
                setBusy(false);
              }} className="text-xs px-2 py-1 bg-rose-600 text-white rounded disabled:opacity-50"
                data-testid="vh-pay-save">
                {busy ? "Kaydediliyor…" : "Ödendi Olarak Kaydet"}
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

/* ─── Helpers ─────────────────────────────────────────────────────────── */
function Field({ label, value, onChange, testid, tiny }) {
  return (
    <label className="block">
      <span className={`${tiny ? "text-[9px]" : "text-[10px]"} uppercase font-bold text-slate-500`}>{label}</span>
      <input value={value} onChange={e=>onChange(e.target.value)}
              className={`w-full border rounded px-2 ${tiny ? "py-0 text-[11px]" : "py-1 text-sm"} font-mono`}
              inputMode="decimal" data-testid={testid}/>
    </label>
  );
}
function Row({ k, v, bold }) {
  return (
    <div className={`flex justify-between items-center ${bold ? "font-bold text-indigo-800" : "text-slate-700"}`}>
      <span className="text-[10px]">{k}</span>
      <span className="font-mono text-[11px]">{money(v)}</span>
    </div>
  );
}

/* iter329.3 — Row with Manual Override controls (admin only) */
function OverrideRow({ k, field, calc, overrides, admin, recordId, onEdit, onRemove, bold }) {
  const ov = overrides[field];
  const hasOverride = ov && ov.manual_value != null && ov.manual_value !== "";
  const effective = hasOverride ? ov.manual_value : calc;
  return (
    <div className={`flex justify-between items-center gap-1 ${bold ? "font-bold text-indigo-800" : "text-slate-700"}`} data-testid={`vh-row-${field}`}>
      <span className="text-[10px] flex-1">{k}
        {hasOverride && <span className="ml-1 text-[9px] px-1 bg-amber-200 text-amber-900 rounded font-bold">M</span>}
      </span>
      {hasOverride ? (
        <div className="flex items-center gap-1 text-[10px]">
          <span className="line-through text-slate-400 font-mono">{money(calc)}</span>
          <span className="font-mono text-amber-800 font-bold">{money(effective)}</span>
        </div>
      ) : (
        <span className="font-mono text-[11px]">{money(effective)}</span>
      )}
      {admin && recordId && (
        <div className="flex gap-0.5">
          <button onClick={onEdit} className="text-[9px] text-indigo-600 hover:underline" title="Manuel Düzelt" data-testid={`vh-override-edit-${field}`}>
            <Edit3 size={9}/>
          </button>
          {hasOverride && (
            <button onClick={onRemove} className="text-[9px] text-rose-600 hover:underline" title="Düzeltmeyi Kaldır" data-testid={`vh-override-remove-${field}`}>
              <X size={9}/>
            </button>
          )}
        </div>
      )}
    </div>
  );
}

/* iter329.3 — Manual Override Modal */
function OverrideModal({ field, existing, onClose, onSave }) {
  const [val, setVal] = useState(existing?.manual_value || "");
  const [reason, setReason] = useState(existing?.reason || "");
  const [busy, setBusy] = useState(false);
  return (
    <div className="fixed inset-0 bg-slate-900/60 z-50 flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-white rounded-lg shadow-2xl max-w-md w-full" onClick={e=>e.stopPropagation()} data-testid="vh-override-modal">
        <div className="p-3 border-b flex justify-between items-center">
          <h3 className="font-bold text-sm">Manuel Düzelt — {field.label}</h3>
          <button onClick={onClose}><X size={16}/></button>
        </div>
        <div className="p-3 space-y-2 text-xs">
          <div className="bg-slate-50 border rounded p-2">
            <div className="text-[10px] uppercase font-bold text-slate-500">Hesaplanan Değer</div>
            <div className="font-mono text-sm">{money(field.calculated)}</div>
          </div>
          <label className="block">
            <span className="text-[10px] uppercase font-bold text-slate-500">Manuel Değer *</span>
            <input value={val} onChange={e=>setVal(e.target.value)} inputMode="decimal"
                    className="w-full border rounded px-2 py-1 text-sm font-mono"
                    data-testid="vh-override-value"/>
          </label>
          <label className="block">
            <span className="text-[10px] uppercase font-bold text-slate-500">Açıklama / Not</span>
            <textarea value={reason} onChange={e=>setReason(e.target.value)} rows={2}
                       className="w-full border rounded px-2 py-1 text-xs"/>
          </label>
          <div className="flex justify-end gap-2 pt-2 border-t">
            <button onClick={onClose} className="text-xs px-2 py-1 bg-slate-200 rounded">Vazgeç</button>
            <button disabled={busy || !val.trim()} onClick={async()=>{
              setBusy(true);
              await onSave(val, reason);
              setBusy(false);
            }} className="text-xs px-2 py-1 bg-indigo-600 text-white rounded disabled:opacity-50"
              data-testid="vh-override-save">
              {busy ? "Kaydediliyor…" : "Kaydet"}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

/* iter329.6 — Obligation type labels (TR) */
const OBL_LABELS = {
  KDV: "KDV",
  KURUM_GECICI: "Kurum/Geçici Vergi",
  YILLIK_KURUMLAR: "Yıllık Kurumlar Vergisi",
  MUHTASAR: "Muhtasar (Kira+Personel Stopajı)",
  OTV: "ÖTV",
  SGK: "SGK",
  DIGER_VERGI: "Diğer Vergi",
};

/* iter329.6 — Upcoming payments panel */
function UpcomingObligationsPanel({ reload, edit, onChange }) {
  const [rows, setRows] = useState([]);
  const [open, setOpen] = useState(true);
  const [pay, setPay] = useState(null);
  const [editDue, setEditDue] = useState(null);
  const { user } = useAuth();
  const admin = ["admin", "system_authority"].includes(user?.role);
  const load = () => api.get("/tax-calculation/obligations/upcoming").then(setRows).catch(()=>setRows([]));
  useEffect(() => { load(); // eslint-disable-next-line
  }, [reload]);
  return (
    <div className="bg-white border border-amber-200 rounded-lg" data-testid="vh-upcoming">
      <div className="flex items-center justify-between p-2 border-b bg-amber-50">
        <button onClick={()=>setOpen(x=>!x)} className="flex items-center gap-1 text-sm font-bold text-amber-900">
          {open ? <ChevronDown size={14}/> : <ChevronRight size={14}/>}
          Yaklaşan Vergi & SGK Ödemeleri ({rows.length})
        </button>
      </div>
      {open && (
        <div className="p-2 max-h-64 overflow-y-auto">
          {rows.length === 0 && <div className="text-slate-400 text-center text-[11px] py-2">Yaklaşan ödeme yok</div>}
          {rows.length > 0 && (
            <table className="w-full text-[11px]">
              <thead>
                <tr className="text-left text-[10px] uppercase text-slate-500 border-b">
                  <th>Vade</th><th>Firma</th><th>Tür</th><th>Dönem</th>
                  <th className="text-right">Tutar</th><th></th>
                </tr>
              </thead>
              <tbody>
                {rows.map(ob => (
                  <tr key={ob.id} className="border-b hover:bg-amber-50" data-testid={`vh-upc-${ob.id}`}>
                    <td className="py-0.5 font-mono text-[10px]">{ob.due_date}</td>
                    <td className="font-bold">{ob.company}</td>
                    <td>{OBL_LABELS[ob.obligation_type] || ob.obligation_type}</td>
                    <td className="text-[10px]">{ob.period_year}/{String(ob.period_month).padStart(2,"0")}{ob.quarter?` ${ob.quarter}`:""}</td>
                    <td className="text-right font-mono">{money(ob.effective_amount)}</td>
                    <td>
                      <div className="flex gap-1 items-center justify-end">
                        {admin && (
                          <button onClick={()=>setEditDue(ob)}
                            className="text-[9px] text-slate-700 hover:underline"
                            title="Vade Tarihini Düzenle"
                            data-testid={`vh-upc-due-${ob.id}`}>Vade</button>
                        )}
                        {edit && (
                          <button onClick={()=>setPay(ob)} className="text-[9px] bg-emerald-600 text-white px-1 py-0.5 rounded"
                            data-testid={`vh-upc-pay-${ob.id}`}>Ödendi</button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      )}
      {pay && (
        <ObligationPayModal
          obligation={pay}
          onClose={()=>setPay(null)}
          onSaved={()=>{ setPay(null); onChange && onChange(); }}
        />
      )}
      {editDue && admin && (
        <DueDateEditModal
          obligation={editDue}
          onClose={()=>setEditDue(null)}
          onSaved={()=>{ setEditDue(null); load(); onChange && onChange(); }}
        />
      )}
    </div>
  );
}

/* iter329.6 — Overdue panel w/ delay interest */
function OverdueObligationsPanel({ reload, edit, admin, onChange }) {
  const [rows, setRows] = useState([]);
  const [open, setOpen] = useState(true);
  const [pay, setPay] = useState(null);
  const [editDue, setEditDue] = useState(null);
  const load = () => api.get("/tax-calculation/obligations/overdue").then(setRows).catch(()=>setRows([]));
  useEffect(() => { load(); // eslint-disable-next-line
  }, [reload]);
  return (
    <div className="bg-white border border-rose-300 rounded-lg" data-testid="vh-overdue">
      <div className="flex items-center justify-between p-2 border-b bg-rose-50">
        <button onClick={()=>setOpen(x=>!x)} className="flex items-center gap-1 text-sm font-bold text-rose-900">
          {open ? <ChevronDown size={14}/> : <ChevronRight size={14}/>}
          Gecikmiş Vergi & SGK ({rows.length})
        </button>
      </div>
      {open && (
        <div className="p-2 max-h-64 overflow-y-auto">
          {rows.length === 0 && <div className="text-slate-400 text-center text-[11px] py-2">Gecikmiş borç yok</div>}
          {rows.length > 0 && (
            <table className="w-full text-[11px]">
              <thead>
                <tr className="text-left text-[10px] uppercase text-slate-500 border-b">
                  <th>Vade</th><th>Firma</th><th>Tür</th><th>Dönem</th>
                  <th className="text-right">Ana Borç</th><th className="text-right">Gecikme</th>
                  <th className="text-right">Toplam</th><th></th>
                </tr>
              </thead>
              <tbody>
                {rows.map(ob => (
                  <tr key={ob.id} className="border-b hover:bg-rose-50" data-testid={`vh-ovd-${ob.id}`}>
                    <td className="py-0.5 font-mono text-[10px]">{ob.due_date}</td>
                    <td className="font-bold">{ob.company}</td>
                    <td>{OBL_LABELS[ob.obligation_type] || ob.obligation_type}</td>
                    <td className="text-[10px]">{ob.period_year}/{String(ob.period_month).padStart(2,"0")}</td>
                    <td className="text-right font-mono">{money(ob.effective_amount)}</td>
                    <td className="text-right font-mono text-rose-700">{money(ob.delay?.interest || ob.delay?.total_interest)}</td>
                    <td className="text-right font-mono font-bold">{money(ob.delay?.total)}</td>
                    <td>
                      <div className="flex gap-1 items-center justify-end">
                        {admin && (
                          <button onClick={()=>setEditDue(ob)}
                            className="text-[9px] text-slate-700 hover:underline"
                            title="Vade Tarihini Düzenle"
                            data-testid={`vh-ovd-due-${ob.id}`}>Vade</button>
                        )}
                        {edit && (
                          <button onClick={()=>setPay(ob)} className="text-[9px] bg-emerald-600 text-white px-1 py-0.5 rounded"
                            data-testid={`vh-ovd-pay-${ob.id}`}>Ödendi</button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      )}
      {pay && (
        <ObligationPayModal
          obligation={pay}
          onClose={()=>setPay(null)}
          onSaved={()=>{ setPay(null); onChange && onChange(); }}
        />
      )}
      {editDue && admin && (
        <DueDateEditModal
          obligation={editDue}
          onClose={()=>setEditDue(null)}
          onSaved={()=>{ setEditDue(null); load(); onChange && onChange(); }}
        />
      )}
    </div>
  );
}

/* iter329.6 — Paid history panel (SABİT ~20 satır container, internal scroll) */
function PaidHistoryPanel({ reload, admin, onChange }) {
  const [data, setData] = useState({total:0, rows:[], page:1, limit:50});
  const [filters, setFilters] = useState({company:"", obligation_type:"", tax_year:""});
  const [page, setPage] = useState(1);
  const [open, setOpen] = useState(false);
  const [reverse, setReverse] = useState(null);
  const load = () => {
    const q = new URLSearchParams();
    q.set("page", page); q.set("limit", 50);
    if (filters.company) q.set("company", filters.company);
    if (filters.obligation_type) q.set("obligation_type", filters.obligation_type);
    if (filters.tax_year) q.set("tax_year", filters.tax_year);
    api.get(`/tax-calculation/obligations/paid-history?${q.toString()}`)
       .then(setData).catch(()=>setData({total:0, rows:[], page:1, limit:50}));
  };
  useEffect(() => { if (open) load(); // eslint-disable-next-line
  }, [open, reload, page, filters.company, filters.obligation_type, filters.tax_year]);
  const totalPages = Math.max(1, Math.ceil(data.total / 50));
  return (
    <div className="bg-white border border-emerald-200 rounded-lg" data-testid="vh-paid-history">
      <div className="flex items-center justify-between p-2 border-b bg-emerald-50">
        <button onClick={()=>setOpen(x=>!x)} className="flex items-center gap-1 text-sm font-bold text-emerald-900">
          {open ? <ChevronDown size={14}/> : <ChevronRight size={14}/>}
          Ödenen Vergi & SGK Geçmişi ({data.total})
        </button>
      </div>
      {open && (
        <div className="p-2">
          <div className="flex gap-2 mb-2 text-[10px]">
            <select value={filters.company} onChange={e=>{setPage(1); setFilters({...filters, company:e.target.value});}}
              className="border rounded px-1 py-0.5" data-testid="vh-ph-f-company">
              <option value="">Tüm Firmalar</option>
              {COMPANIES.map(c=><option key={c}>{c}</option>)}
            </select>
            <select value={filters.obligation_type} onChange={e=>{setPage(1); setFilters({...filters, obligation_type:e.target.value});}}
              className="border rounded px-1 py-0.5" data-testid="vh-ph-f-type">
              <option value="">Tüm Türler</option>
              {Object.keys(OBL_LABELS).map(t=><option key={t} value={t}>{OBL_LABELS[t]}</option>)}
            </select>
            <input type="number" value={filters.tax_year} onChange={e=>{setPage(1); setFilters({...filters, tax_year:e.target.value});}}
              placeholder="Yıl" className="border rounded px-1 py-0.5 w-16" data-testid="vh-ph-f-year"/>
          </div>
          <div className="overflow-y-auto border rounded" style={{height: "560px"}} data-testid="vh-ph-scroll">
            <table className="w-full text-[11px]">
              <thead className="sticky top-0 bg-slate-50 shadow-sm">
                <tr className="text-left text-[10px] uppercase text-slate-500 border-b">
                  <th className="p-1">Öd. Tarih</th>
                  <th>Firma</th><th>Tür</th><th>Dönem</th>
                  <th className="text-right">Hesaplanan</th>
                  <th className="text-right">Ödenen</th>
                  <th>Vade</th>
                  <th className="text-right">Gecikme</th>
                  <th className="text-right">Toplam</th>
                  <th>Not</th>
                  {admin && <th></th>}
                </tr>
              </thead>
              <tbody>
                {data.rows.map(ob => (
                  <tr key={ob.id} className="border-b hover:bg-emerald-50" data-testid={`vh-ph-${ob.id}`}>
                    <td className="p-1 font-mono text-[10px]">{ob.payment_date || "-"}</td>
                    <td className="font-bold">{ob.company}</td>
                    <td>{OBL_LABELS[ob.obligation_type] || ob.obligation_type}</td>
                    <td className="text-[10px]">{ob.period_year}/{String(ob.period_month).padStart(2,"0")}{ob.quarter?` ${ob.quarter}`:""}</td>
                    <td className="text-right font-mono">{money(ob.effective_amount)}</td>
                    <td className="text-right font-mono text-emerald-700">{money(ob.actual_paid_amount)}</td>
                    <td className="font-mono text-[10px]">{ob.due_date}</td>
                    <td className="text-right font-mono text-slate-500">{money(ob.delay?.interest || ob.delay?.total_interest || 0)}</td>
                    <td className="text-right font-mono font-bold">{money(ob.delay?.total || ob.actual_paid_amount)}</td>
                    <td className="text-[9px] italic text-slate-500 truncate max-w-[120px]">{ob.note || ""}</td>
                    {admin && (
                      <td>
                        <button onClick={()=>setReverse(ob)} className="text-[9px] text-rose-700 hover:underline"
                          title="Ödemeyi Geri Al" data-testid={`vh-ph-rev-${ob.id}`}>Geri Al</button>
                      </td>
                    )}
                  </tr>
                ))}
                {data.rows.length === 0 && <tr><td colSpan={admin?11:10} className="text-center py-3 text-slate-400">Kayıt yok</td></tr>}
              </tbody>
            </table>
          </div>
          <div className="flex items-center justify-between mt-2 text-[10px]">
            <span className="text-slate-500">Toplam {data.total} kayıt — Sayfa {page}/{totalPages}</span>
            <div className="flex gap-1">
              <button disabled={page<=1} onClick={()=>setPage(p=>p-1)}
                className="border rounded px-2 py-0.5 disabled:opacity-30">◀</button>
              <button disabled={page>=totalPages} onClick={()=>setPage(p=>p+1)}
                className="border rounded px-2 py-0.5 disabled:opacity-30">▶</button>
            </div>
          </div>
        </div>
      )}
      {reverse && admin && (
        <ReversalModal
          obligation={reverse}
          onClose={()=>setReverse(null)}
          onSaved={()=>{ setReverse(null); onChange && onChange(); load(); }}
        />
      )}
    </div>
  );
}

/* iter329.6 — Generic obligation pay modal */
function ObligationPayModal({ obligation, onClose, onSaved }) {
  const [amount, setAmount] = useState(obligation.effective_amount || "0");
  const [paymentDate, setPaymentDate] = useState(new Date().toISOString().slice(0,10));
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);
  const save = async () => {
    setBusy(true);
    try {
      await api.post(`/tax-calculation/obligations/${obligation.id}/pay`,
        { actual_paid_amount: amount, payment_date: paymentDate, note: note || null });
      onSaved();
    } catch(e) { alert(e?.response?.data?.detail || "Ödeme kaydı başarısız"); }
    finally { setBusy(false); }
  };
  return (
    <div className="fixed inset-0 bg-slate-900/60 z-50 flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-white rounded-lg shadow-2xl max-w-md w-full" onClick={e=>e.stopPropagation()} data-testid="vh-obl-pay-modal">
        <div className="p-3 border-b flex justify-between items-center">
          <h3 className="font-bold text-sm">Ödendi: {OBL_LABELS[obligation.obligation_type] || obligation.obligation_type} — {obligation.company}</h3>
          <button onClick={onClose}><X size={16}/></button>
        </div>
        <div className="p-3 space-y-2 text-xs">
          <div className="bg-slate-50 border rounded p-2 space-y-0.5">
            <div className="flex justify-between"><span>Dönem</span>
              <span className="font-mono">{obligation.period_year}/{String(obligation.period_month).padStart(2,"0")}{obligation.quarter?` ${obligation.quarter}`:""}</span></div>
            <div className="flex justify-between"><span>Vade</span>
              <span className="font-mono">{obligation.due_date}</span></div>
            <div className="flex justify-between"><span>Hesaplanan</span>
              <span className="font-mono">{money(obligation.effective_amount)}</span></div>
            {obligation.delay && (
              <div className="flex justify-between text-rose-700"><span>Bugüne Gecikme</span>
                <span className="font-mono">{money(obligation.delay.interest || obligation.delay.total_interest)}</span></div>
            )}
          </div>
          <label className="block">
            <span className="text-[10px] uppercase font-bold text-slate-500">Gerçekte Ödenen Tutar *</span>
            <input value={amount} onChange={e=>setAmount(e.target.value)} inputMode="decimal"
              className="w-full border rounded px-2 py-1 text-sm font-mono" data-testid="vh-obl-amount"/>
          </label>
          <label className="block">
            <span className="text-[10px] uppercase font-bold text-slate-500">Ödeme Tarihi *</span>
            <input type="date" value={paymentDate} onChange={e=>setPaymentDate(e.target.value)}
              className="w-full border rounded px-2 py-1 text-xs font-mono" data-testid="vh-obl-date"/>
          </label>
          <label className="block">
            <span className="text-[10px] uppercase font-bold text-slate-500">Not</span>
            <textarea value={note} onChange={e=>setNote(e.target.value)} rows={2}
              className="w-full border rounded px-2 py-1 text-xs"/>
          </label>
          <div className="flex justify-end gap-2 pt-2 border-t">
            <button onClick={onClose} className="text-xs px-2 py-1 bg-slate-200 rounded">Vazgeç</button>
            <button disabled={busy || !amount || !paymentDate} onClick={save}
              className="text-xs px-2 py-1 bg-emerald-600 text-white rounded disabled:opacity-50"
              data-testid="vh-obl-save">
              {busy ? "Kaydediliyor…" : "Ödendi Olarak Kaydet"}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

/* iter329.6 — Reversal modal (admin only) */
function ReversalModal({ obligation, onClose, onSaved }) {
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState(false);
  const save = async () => {
    setBusy(true);
    try {
      await api.post(`/tax-calculation/obligations/${obligation.id}/reverse`,
        { reason: reason || null });
      onSaved();
    } catch(e) { alert(e?.response?.data?.detail || "Reversal başarısız"); }
    finally { setBusy(false); }
  };
  return (
    <div className="fixed inset-0 bg-slate-900/60 z-50 flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-white rounded-lg shadow-2xl max-w-md w-full" onClick={e=>e.stopPropagation()} data-testid="vh-rev-modal">
        <div className="p-3 border-b flex justify-between items-center">
          <h3 className="font-bold text-sm text-rose-800">Ödemeyi Geri Al</h3>
          <button onClick={onClose}><X size={16}/></button>
        </div>
        <div className="p-3 space-y-2 text-xs">
          <div className="bg-rose-50 border border-rose-200 rounded p-2 text-[10px]">
            <b>Dikkat:</b> Bu işlem yalnız Vergi Hesabı sayfasındaki ödeme durumunu geri alır.
            Başka hiçbir muhasebe/banka hareketi oluşturmaz. Audit kaydedilir.
          </div>
          <div className="bg-slate-50 border rounded p-2 space-y-0.5">
            <div className="flex justify-between"><span>Firma / Tür</span>
              <span className="font-mono">{obligation.company} — {OBL_LABELS[obligation.obligation_type] || obligation.obligation_type}</span></div>
            <div className="flex justify-between"><span>Ödeme Tarihi</span>
              <span className="font-mono">{obligation.payment_date}</span></div>
            <div className="flex justify-between"><span>Ödenen Tutar</span>
              <span className="font-mono">{money(obligation.actual_paid_amount)}</span></div>
          </div>
          <label className="block">
            <span className="text-[10px] uppercase font-bold text-slate-500">Sebep / Açıklama</span>
            <textarea value={reason} onChange={e=>setReason(e.target.value)} rows={3}
              className="w-full border rounded px-2 py-1 text-xs" data-testid="vh-rev-reason"/>
          </label>
          <div className="flex justify-end gap-2 pt-2 border-t">
            <button onClick={onClose} className="text-xs px-2 py-1 bg-slate-200 rounded">Vazgeç</button>
            <button disabled={busy} onClick={save}
              className="text-xs px-2 py-1 bg-rose-600 text-white rounded disabled:opacity-50"
              data-testid="vh-rev-save">
              {busy ? "İşleniyor…" : "Ödemeyi Geri Al"}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}


/* iter329.7 — Due Date Edit Modal (admin-only, due_date single-field) */
function DueDateEditModal({ obligation, onClose, onSaved }) {
  const [due, setDue] = useState(obligation.due_date || "");
  const [busy, setBusy] = useState(false);
  const save = async () => {
    if (!due) return;
    setBusy(true);
    try {
      await api.patch(`/tax-calculation/obligations/${obligation.id}/due-date`,
        { due_date: due });
      onSaved();
    } catch(e) { alert(e?.response?.data?.detail || "Vade kaydı başarısız"); }
    finally { setBusy(false); }
  };
  return (
    <div className="fixed inset-0 bg-slate-900/60 z-50 flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-white rounded-lg shadow-2xl max-w-sm w-full" onClick={e=>e.stopPropagation()} data-testid="vh-due-modal">
        <div className="p-3 border-b flex justify-between items-center">
          <h3 className="font-bold text-sm">Vade Tarihini Düzenle</h3>
          <button onClick={onClose}><X size={16}/></button>
        </div>
        <div className="p-3 space-y-2 text-xs">
          <div className="bg-slate-50 border rounded p-2 space-y-0.5">
            <div className="flex justify-between"><span>Firma / Tür</span>
              <span className="font-mono">{obligation.company} — {OBL_LABELS[obligation.obligation_type] || obligation.obligation_type}</span></div>
            <div className="flex justify-between"><span>Dönem</span>
              <span className="font-mono">{obligation.period_year}/{String(obligation.period_month).padStart(2,"0")}{obligation.quarter?` ${obligation.quarter}`:""}</span></div>
            <div className="flex justify-between"><span>Mevcut Vade</span>
              <span className="font-mono">{obligation.due_date}</span></div>
          </div>
          <label className="block">
            <span className="text-[10px] uppercase font-bold text-slate-500">Yeni Vade Tarihi *</span>
            <input type="date" value={due} onChange={e=>setDue(e.target.value)}
              className="w-full border rounded px-2 py-1 text-sm font-mono" data-testid="vh-due-input"/>
          </label>
          <div className="text-[9px] text-slate-500 italic">
            Sadece vade tarihi güncellenir. Ödeme bilgileri (durumu, tarihi, tutarı) etkilenmez.
            Yeni vadeye göre upcoming/overdue sınıflandırması ve gecikme faizi otomatik yeniden hesaplanır.
          </div>
          <div className="flex justify-end gap-2 pt-2 border-t">
            <button onClick={onClose} className="text-xs px-2 py-1 bg-slate-200 rounded">Vazgeç</button>
            <button disabled={busy || !due} onClick={save}
              className="text-xs px-2 py-1 bg-indigo-600 text-white rounded disabled:opacity-50"
              data-testid="vh-due-save">
              {busy ? "Kaydediliyor…" : "Vadeyi Güncelle"}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

